import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { BlogPost, categories, FALLBACK_IMAGE } from '../data/blog';

/**
 * BLOCK PART: journal card
 * ACF: rendered from a `post` record inside `block_journal_grid`.
 * Variants: `featured` (two-column hero card) and default (grid card).
 */

interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
}
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
export function BlogCard({
  post,
  featured = false
}: BlogCardProps) {
  const category = categories.find(item => item.id === post.category)!;
  const Icon = category.icon;
  const [imgSrc, setImgSrc] = useState(post.image);

  // Keep the image in sync when the post prop changes (e.g. the featured post
  // switching as category filters change).
  useEffect(() => {
    setImgSrc(post.image);
  }, [post.image]);
  const handleError = () => {
    if (imgSrc !== FALLBACK_IMAGE) setImgSrc(FALLBACK_IMAGE);
  };
  const CategoryLabel = () => <span className="inline-flex items-center gap-2 text-[11px] font-medium tracking-[0.25em] text-sage-700 uppercase" data-id="element-235" data-mp="39nk0p75qtqt5">
      <Icon size={14} strokeWidth={1.5} aria-hidden="true" data-id="element-236" data-mp="3ek0rya88olag" />
      {category.name}
    </span>;
  if (featured) {
    return <article data-id="element-237" data-mp="3nm9ykp0dzuik">
        <Link to={`/journal/${post.slug}`} className="group grid grid-cols-1 overflow-hidden rounded-3xl bg-white shadow-soft transition-shadow hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-sage-700 focus:ring-offset-4 lg:grid-cols-2" data-id="element-238" data-mp="3nmjt8pwdmpul">
          <div className="relative aspect-[4/3] overflow-hidden bg-cream-100" data-id="element-239" data-mp="191r3105m37f">
            <img src={imgSrc} onError={handleError} alt={post.title} loading="lazy" decoding="async" width={1000} height={750} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-240" data-mp="650m37fzmjgt" />
            <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-charcoal shadow-sm backdrop-blur-sm" data-id="element-241" data-mp="278jfpoi0pfgy">
              <Icon size={14} strokeWidth={1.5} aria-hidden="true" data-id="element-242" data-mp="2g8puddpgclhp" />
              {category.name}
            </span>
          </div>
          <div className="flex flex-col justify-center p-10 lg:p-14" data-id="element-243" data-mp="2uwswgc0djd18">
            <div className="mb-6 flex items-center gap-4" data-id="element-244" data-mp="2pyrha262jxv1">
              <CategoryLabel data-id="element-245" data-mp="2py9boexio7ag" />
              <span aria-hidden="true" className="h-1 w-1 rounded-full bg-charcoal-50/30" data-id="element-246" data-mp="33yabdr21p8oc" />
              <time dateTime={post.date} className="text-xs tracking-wide text-charcoal-50" data-id="element-247" data-mp="2zxqfeqt7kb3s">
                {formatDate(post.date)}
              </time>
            </div>
            <h3 className="mb-5 font-serif text-3xl leading-tight text-charcoal transition-colors group-hover:text-sage-700 md:text-4xl" data-id="element-248" data-mp="15k8pix5bz25l">
              {post.title}
            </h3>
            <p className="mb-8 font-light leading-relaxed text-charcoal-50" data-id="element-249" data-mp="zf9in76hl0bp">
              {post.excerpt}
            </p>
            <span className="inline-flex items-center text-sm tracking-wide text-sage-700" data-id="element-250" data-mp="loeeu9auh9ez">
              Read the full reflection
              <ArrowRight size={16} strokeWidth={1.5} className="ml-2 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-251" data-mp="bqq8sn7sgfhi" />
            </span>
          </div>
        </Link>
      </article>;
  }
  return <article className="h-full" data-id="element-252" data-mp="7p50lf678pdn">
      <Link to={`/journal/${post.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-soft transition-all hover:-translate-y-1 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-sage-700 focus:ring-offset-4" data-id="element-253" data-mp="7od5s6kfevof">
        <div className="aspect-[16/10] overflow-hidden bg-cream-200" data-id="element-254" data-mp="20o8a8bnrids">
          <img src={imgSrc} onError={handleError} alt={post.title} loading="lazy" decoding="async" width={800} height={500} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-255" data-mp="1ytxhmu3p6nf" />
        </div>
        <div className="flex flex-grow flex-col p-7" data-id="element-256" data-mp="3f7yg5piauaq2">
          <div className="mb-4" data-id="element-257" data-mp="3f9j7z7d8bfk8">
            <CategoryLabel data-id="element-258" data-mp="3agzsu5yw8cdb" />
          </div>
          <h3 className="mb-3 font-serif text-xl leading-snug text-charcoal transition-colors group-hover:text-sage-700" data-id="element-259" data-mp="3af5amzf0aycx">
            {post.title}
          </h3>
          <p className="mb-6 flex-grow text-sm font-light leading-relaxed text-charcoal-50" data-id="element-260" data-mp="30j26jo0lgpnc">
            {post.excerpt}
          </p>
          <div className="mt-auto flex items-center justify-between border-t border-cream-200 pt-4" data-id="element-261" data-mp="2wgfbzfcjgqmg">
            <time dateTime={post.date} className="text-xs tracking-wide text-charcoal-50" data-id="element-262" data-mp="2ridsitb7apag">
              {formatDate(post.date)}
            </time>
            <span className="inline-flex items-center text-sm text-sage-700" data-id="element-263" data-mp="1bxl9c1lqw44m">
              Read
              <ArrowRight size={14} strokeWidth={1.5} className="ml-1 transition-transform group-hover:translate-x-0.5" aria-hidden="true" data-id="element-264" data-mp="1g1t2qc35e896" />
            </span>
          </div>
        </div>
      </Link>
    </article>;
}